import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base.service';
import { HealthEventResponse, HealthEventApi, AppointmentResponse, AppointmentApi } from '../interfaces/health-event.interface';

@Injectable({
  providedIn: 'root',
})
export class HealthEventService extends BaseService {
  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Récupère les rendez-vous passés pour un passeport donné
   * @param passportNumber - Le numéro de passeport
   * @param page - Page actuelle (optionnel, défaut: 1)
   * @param limit - Nombre d'éléments par page (optionnel, défaut: 20)
   * @returns Observable contenant la réponse avec les rendez-vous passés et la pagination
   */
  getPastAppointments(
    passportNumber: string,
    page: number = 1,
    limit: number = 12
  ): Observable<AppointmentResponse> {
    const httpParams = new HttpParams()
      .set('timeFilter', 'past')
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<AppointmentResponse>(
      `${this.baseUrl}/appointments`,
      {
        headers: this.getAuthHeaders(),
        params: httpParams,
      }
    );
  }

  /**
   * Récupère les rendez-vous à venir pour un passeport donné
   * @param passportNumber - Le numéro de passeport
   * @param page - Page actuelle (optionnel, défaut: 1)
   * @param limit - Nombre d'éléments par page (optionnel, défaut: 20)
   * @returns Observable contenant la réponse avec les rendez-vous à venir et la pagination
   */
  getUpcomingAppointments(
    passportNumber: string,
    page: number = 1,
    limit: number = 12
  ): Observable<AppointmentResponse> {
    const httpParams = new HttpParams()
      .set('timeFilter', 'upcoming')
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<AppointmentResponse>(
      `${this.baseUrl}/appointments`,
      {
        headers: this.getAuthHeaders(),
        params: httpParams,
      }
    );
  }

  /**
   * Récupère les événements de santé pour un passeport donné avec pagination
   * @param passportNumber - Le numéro de passeport
   * @param page - Page actuelle (optionnel, défaut: 1)
   * @param limit - Nombre d'éléments par page (optionnel, défaut: 20)
   * @returns Observable contenant la réponse avec les événements et la pagination
   */
  getHealthEvents(
    passportNumber: string,
    page: number = 1,
    limit: number = 20
  ): Observable<HealthEventResponse> {
    const httpParams = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<HealthEventResponse>(
      `${this.baseUrl}/health-events/passport/${passportNumber}`,
      {
        headers: this.getAuthHeaders(),
        params: httpParams,
      }
    );
  }

  /**
   * Récupère tous les événements de santé pour un passeport (sans pagination)
   * @param passportNumber - Le numéro de passeport
   * @returns Observable contenant tous les événements
   */
  getAllHealthEvents(passportNumber: string): Observable<HealthEventApi[]> {
    return this.http.get<HealthEventApi[]>(
      `${this.baseUrl}/health-events/passport/${passportNumber}`,
      {
        headers: this.getAuthHeaders(),
      }
    );
  }

  /**
   * Récupère un événement de santé par son ID
   * @param eventId - L'ID de l'événement
   * @returns Observable contenant l'événement
   */
  getHealthEventById(eventId: string): Observable<HealthEventApi> {
    return this.http.get<HealthEventApi>(
      `${this.baseUrl}/health-events/${eventId}`,
      {
        headers: this.getAuthHeaders(),
      }
    );
  }

  /**
   * Rattache un ou plusieurs documents a un evenement de sante.
   */
  attachDocuments(eventId: string, documentIds: string[]): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/health-events/${eventId}/documents`,
      { documentIds },
      {
        headers: this.getAuthHeaders(),
      }
    );
  }

  /**
   * Rattache un ou plusieurs documents a un evenement de sante.
   */
  getAttachedDocuments(eventId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/health-events/${eventId}/documents`,
      {
        headers: this.getAuthHeaders(),
      }
    );
  }
}
